import { validatePasswordLength } from '@chorepop/core';
import { type Component, Show, createSignal } from 'solid-js';
import { ActionScreen } from '../components/ActionScreen';

type Props = {
  displayName: string;
  onComplete: (password: string) => Promise<void>;
  onCancel: () => void;
};

/**
 * Final step of a cloud restore. Google/Apple proves ownership of the cloud
 * family; this screen establishes a new device-local secret before the app
 * grants access to the restored ledger.
 */
export const RestoreDevicePasswordScreen: Component<Props> = (props) => {
  const [password, setPassword] = createSignal('');
  const [confirmation, setConfirmation] = createSignal('');
  const [submitting, setSubmitting] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);

  const submit = async (event: Event) => {
    event.preventDefault();
    const value = password();
    const validation = validatePasswordLength(value);
    if (validation) {
      setError(validation.message);
      return;
    }
    if (value !== confirmation()) {
      setError('The passwords do not match.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await props.onComplete(value);
      setPassword('');
      setConfirmation('');
    } catch (cause: unknown) {
      setError(cause instanceof Error ? cause.message : 'Your family could not be restored.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ActionScreen
      eyebrow="New device"
      title="Protect this device"
      onBack={props.onCancel}
      class="action-screen--settings"
    >
      <form class="settings-card" onSubmit={submit} novalidate data-testid="restore-device-form">
        <section class="settings-section">
          <h2>Welcome back, {props.displayName}</h2>
          <p class="settings-section-intro">
            Your family data has been restored. Create a password for this device before opening it.
            Google or Apple will not be used for everyday sign-in.
          </p>
          <label class="field" for="restore-device-password">
            <span class="field-label">New device password</span>
            <input
              id="restore-device-password"
              type="password"
              autocomplete="new-password"
              required
              disabled={submitting()}
              value={password()}
              onInput={(event) => setPassword(event.currentTarget.value)}
              data-testid="restore-device-password"
            />
            <span class="field-hint">
              At least 10 characters. You can also set a guardian PIN later.
            </span>
          </label>
          <label class="field" for="restore-device-password-confirmation">
            <span class="field-label">Confirm device password</span>
            <input
              id="restore-device-password-confirmation"
              type="password"
              autocomplete="new-password"
              required
              disabled={submitting()}
              value={confirmation()}
              onInput={(event) => setConfirmation(event.currentTarget.value)}
              data-testid="restore-device-password-confirmation"
            />
          </label>
          <Show when={error()}>
            {(message) => (
              <div class="form-error" role="alert">
                {message()}
              </div>
            )}
          </Show>
          <button
            type="submit"
            class="primary"
            disabled={submitting() || password().length === 0 || confirmation().length === 0}
            data-testid="restore-device-complete"
          >
            {submitting() ? 'Protecting…' : 'Open my family'}
          </button>
        </section>
      </form>
    </ActionScreen>
  );
};
